import type {
  ChatTrigger,
  EmbedEventBatch,
  EmbedKind,
  EndSessionResponse,
  EventsResponse,
  SessionResponse,
  SessionTranscript,
  StartSessionBody,
} from "@workspace/contracts/embed"

import { readReplyStream, type Reply } from "./stream"

/**
 * The calls the runtime makes (`apps/api`'s `embed` module, plan §1.1).
 * Every one is unauthenticated and origin-gated: the browser sends
 * `Origin` on its own, the server answers 403 `origin_not_allowed` for a
 * page that is not on the owner's verified domains, and the runtime reads
 * that as a JSON error rather than an opaque CORS failure (`app.ts`). The
 * session calls after the first carry the session's Bearer token, which
 * is bound to the origin it was minted on.
 */

export interface Api {
  /** The API origin - `https://api.usereq.com`, or the element's `api-url`. */
  base: string
}

/** What the API says went wrong: its `error.code` and sentence, with the status. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string
  ) {
    super(message)
  }
}

async function fail(response: Response): Promise<never> {
  let code = "request_failed"
  let message = `The request failed (${response.status}).`
  try {
    const body = (await response.json()) as {
      error?: { code?: string; message?: string }
    }
    if (body.error?.code) code = body.error.code
    if (body.error?.message) message = body.error.message
  } catch {
    // The body was not JSON - the status is all there is to say.
  }
  throw new ApiError(response.status, code, message)
}

const json = (body: unknown, headers: Record<string, string> = {}) => ({
  method: "POST",
  headers: { "Content-Type": "application/json", ...headers },
  body: JSON.stringify(body),
})

/** `GET /api/embed/{kind}/{id}` - the active snapshot, as JSON, unchecked. */
export async function fetchSnapshot(
  api: Api,
  kind: EmbedKind,
  id: string
): Promise<unknown> {
  const response = await fetch(`${api.base}/api/embed/${kind}/${id}`)
  if (!response.ok) return fail(response)
  return response.json()
}

/**
 * `GET /api/embed/agent/{agentId}` - the active snapshot of the agent's one
 * live chat surface: a WIDGET payload, so everything after the fetch is the
 * same as for `widget-id`. The previous platform's tag named the agent.
 */
export async function fetchAgentSnapshot(
  api: Api,
  agentId: string
): Promise<unknown> {
  const response = await fetch(`${api.base}/api/embed/agent/${agentId}`)
  if (!response.ok) return fail(response)
  return response.json()
}

/** `POST /api/embed/sessions` - open the conversation, get its Bearer token. */
export async function startSession(
  api: Api,
  body: StartSessionBody
): Promise<SessionResponse> {
  const response = await fetch(`${api.base}/api/embed/sessions`, json(body))
  if (!response.ok) return fail(response)
  return response.json() as Promise<SessionResponse>
}

const bearer = (token: string) => ({ Authorization: `Bearer ${token}` })

/**
 * `GET /api/embed/sessions/current` - the conversation a kept token names,
 * read back after a reload: its turns, whether it has ended.
 */
export async function fetchTranscript(
  api: Api,
  token: string
): Promise<SessionTranscript> {
  const response = await fetch(`${api.base}/api/embed/sessions/current`, {
    headers: bearer(token),
  })
  if (!response.ok) return fail(response)
  return response.json() as Promise<SessionTranscript>
}

/** `POST /api/embed/sessions/current/end` - the visitor stops the conversation. */
export async function endSession(
  api: Api,
  token: string
): Promise<EndSessionResponse> {
  const response = await fetch(`${api.base}/api/embed/sessions/current/end`, {
    method: "POST",
    headers: bearer(token),
  })
  if (!response.ok) return fail(response)
  return response.json() as Promise<EndSessionResponse>
}

/**
 * `POST /api/embed/chat` - ONE new visitor turn under the session's token,
 * answered with the agent's whole reply as an AI SDK UI-message stream. The
 * body arrives once the reply is stored, so this resolves with the reply
 * complete: its id and its paragraphs, for the page to reveal on the
 * contract's cadence.
 */
export async function sendTurn(
  api: Api,
  token: string,
  message: { id: string; text: string },
  trigger: ChatTrigger,
  signal: AbortSignal
): Promise<Reply> {
  const response = await fetch(`${api.base}/api/embed/chat`, {
    ...json(
      {
        message: {
          id: message.id,
          role: "user",
          parts: [{ type: "text", text: message.text }],
        },
        trigger,
      },
      bearer(token)
    ),
    signal,
  })
  if (!response.ok || !response.body) return fail(response)
  return readReplyStream(response.body)
}

/**
 * `POST /api/embed/events` - a batch, `keepalive` so a flush on `pagehide`
 * outlives the page. The answer is the batch's fate, never a failure the
 * page has to act on; a refused batch is simply dropped.
 */
export async function postEvents(
  api: Api,
  batch: EmbedEventBatch
): Promise<EventsResponse | null> {
  try {
    const response = await fetch(`${api.base}/api/embed/events`, {
      ...json(batch),
      keepalive: true,
    })
    if (!response.ok) return null
    return (await response.json()) as EventsResponse
  } catch {
    return null
  }
}
